import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Trophy, Plus, Trash2, Users, Sparkles, Award, Edit3, X, Save } from "lucide-react";

const emptyContest = () => ({
  name: "", description: "", prize_description: "", prize_image_url: "",
  start_at: "", end_at: "", points_reward: 25, points_cost: 0,
  max_entries_per_member: 1, is_active: true, winner_voucher_value: 0,
});

export default function ContestsAdmin() {
  const [items, setItems] = useState([]);
  const [edit, setEdit] = useState(null);
  const [entries, setEntries] = useState({ contestId: null, list: [] });

  const load = () => api.get("/admin/contests").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!edit.name?.trim()) { toast.error("Name pflichtfeld"); return; }
    try {
      if (edit.id) await api.patch(`/admin/contests/${edit.id}`, edit);
      else await api.post("/admin/contests", edit);
      toast.success("Gespeichert"); setEdit(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };
  const remove = async (id) => {
    if (!window.confirm("Gewinnspiel + alle Teilnahmen löschen?")) return;
    try { await api.delete(`/admin/contests/${id}`); toast.success("Gelöscht"); load(); }
    catch (e) { toast.error("Fehler"); }
  };
  const drawWinner = async (c) => {
    if (!window.confirm(`Gewinner für „${c.name}" auslosen? Danach wird das Gewinnspiel geschlossen.`)) return;
    try {
      const { data } = await api.post(`/admin/contests/${c.id}/draw-winner`);
      toast.success(`Gewinner: ${data.winner_entry.guest_name}${data.voucher_code ? ` · Voucher ${data.voucher_code}` : ""}`);
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Fehler"); }
  };
  const showEntries = async (c) => {
    try {
      const { data } = await api.get(`/admin/contests/${c.id}/entries`);
      setEntries({ contestId: c.id, name: c.name, list: data });
    } catch { toast.error("Konnte Teilnahmen nicht laden"); }
  };

  return (
    <div className="space-y-6" data-testid="contests-admin-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <Trophy size={22} className="text-[#D4AF37]" />
          <div>
            <h1 className="font-heading text-3xl">Gewinnspiele</h1>
            <div className="text-[10px] uppercase tracking-[0.32em] text-[#A8A398] mt-1">Mitglieder sammeln Punkte · Gewinner werden ausgelost</div>
          </div>
        </div>
        <button onClick={() => setEdit(emptyContest())} data-testid="contest-new-btn"
          className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black px-5 py-2 rounded text-[11px] uppercase tracking-widest">
          <Plus size={13} /> Neues Gewinnspiel
        </button>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((c) => (
          <div key={c.id} data-testid={`contest-card-${c.id}`}
            className={`card-lux p-4 flex flex-col ${!c.is_active ? "opacity-70" : ""}`}>
            {c.prize_image_url && <img src={c.prize_image_url} alt="" className="w-full h-32 object-cover rounded mb-3" />}
            <div className="flex items-center justify-between gap-2 flex-wrap mb-2">
              <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] flex items-center gap-1">
                <Sparkles size={11} /> {c.points_reward} Punkte
              </div>
              {!c.is_active && <span className="text-[9px] text-[#EE9CA1] uppercase tracking-widest">Geschlossen</span>}
            </div>
            <div className="font-heading text-lg">{c.name}</div>
            <div className="text-xs text-[#A8A398] mt-1 line-clamp-2">{c.description}</div>
            <div className="text-xs text-[#EEDC82] mt-2"><Award size={11} className="inline mr-1" /> {c.prize_description}</div>
            <div className="text-[10px] text-[#575148] mt-3 space-y-0.5">
              {c.end_at && <div>Ende: {new Date(c.end_at).toLocaleString("de-DE")}</div>}
              <div><Users size={10} className="inline mr-1" />{c.total_entries || 0} Teilnahmen</div>
              {c.winner_guest_id && <div className="text-[#D4AF37]">★ Gewinner gezogen</div>}
            </div>
            <div className="mt-3 flex gap-1 flex-wrap">
              <button onClick={() => setEdit(c)} className="flex-1 text-[10px] uppercase tracking-widest py-1.5 border border-[#2A2A2A] rounded hover:border-[#D4AF37]">
                <Edit3 size={11} className="inline mr-1" /> Bearbeiten
              </button>
              <button onClick={() => showEntries(c)} className="text-[10px] uppercase tracking-widest py-1.5 px-2 border border-[#2A2A2A] rounded hover:border-[#D4AF37]">
                <Users size={11} />
              </button>
              {!c.winner_guest_id && (c.total_entries > 0) && (
                <button onClick={() => drawWinner(c)} data-testid={`contest-draw-${c.id}`}
                  className="text-[10px] uppercase tracking-widest py-1.5 px-2 border border-[#8B7328] text-[#EEDC82] rounded hover:bg-[#D4AF37]/10">
                  <Trophy size={11} />
                </button>
              )}
              <button onClick={() => remove(c.id)} className="text-[10px] py-1.5 px-2 border border-[#2A2A2A] rounded hover:border-[#7A1921]">
                <Trash2 size={11} />
              </button>
            </div>
          </div>
        ))}
        {!items.length && (
          <div className="card-lux p-10 text-center text-[#A8A398] md:col-span-2 lg:col-span-3">
            <Trophy size={40} className="mx-auto text-[#575148] mb-3" />
            Noch kein Gewinnspiel angelegt. Klick oben rechts auf &bdquo;Neues Gewinnspiel&ldquo;, um eins zu erstellen.
          </div>
        )}
      </div>

      {edit && <EditModal edit={edit} setEdit={setEdit} save={save} />}
      {entries.contestId && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEntries({ contestId: null, list: [] })}>
          <div className="card-lux max-w-lg w-full max-h-[80vh] overflow-auto p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="font-heading text-xl">Teilnahmen · {entries.name}</div>
              <button onClick={() => setEntries({ contestId: null, list: [] })} className="p-1 hover:text-[#D4AF37]"><X size={14} /></button>
            </div>
            {!entries.list.length ? (
              <div className="text-sm text-[#A8A398]">Noch keine Teilnahmen.</div>
            ) : (
              <ul className="space-y-1 text-sm">
                {entries.list.map((e) => (
                  <li key={e.id} className="flex justify-between py-1 border-b border-[#1A1A1A]">
                    <span>{e.guest_name}</span>
                    <span className="text-[#575148] text-xs">{new Date(e.created_at).toLocaleString("de-DE")}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function EditModal({ edit, setEdit, save }) {
  const up = (k) => (v) => setEdit({ ...edit, [k]: v });
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setEdit(null)}>
      <div className="card-lux max-w-2xl w-full max-h-[90vh] overflow-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-2 flex-wrap mb-4">
          <div className="font-heading text-2xl">{edit.id ? "Gewinnspiel bearbeiten" : "Neues Gewinnspiel"}</div>
          <button onClick={() => setEdit(null)} className="p-1 hover:text-[#D4AF37]"><X size={16} /></button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <F full label="Name" v={edit.name} on={up("name")} testid="contest-name" />
          <F full label="Beschreibung" v={edit.description} on={up("description")} textarea rows={2} />
          <F full label="Gewinn (Text)" v={edit.prize_description} on={up("prize_description")} placeholder="z.B. VIP Ticket + Popcorn XL" testid="contest-prize" />
          <F full label="Bild-URL (optional)" v={edit.prize_image_url} on={up("prize_image_url")} placeholder="https://…" />
          <F label="Startzeit (optional)" v={(edit.start_at || "").slice(0, 16)} on={(v) => up("start_at")(v ? new Date(v).toISOString() : "")} type="datetime-local" />
          <F label="Endzeit (optional)" v={(edit.end_at || "").slice(0, 16)} on={(v) => up("end_at")(v ? new Date(v).toISOString() : "")} type="datetime-local" />
          <F label="Punkte-Belohnung" v={edit.points_reward} on={(v) => up("points_reward")(parseInt(v) || 0)} type="number" testid="contest-reward" />
          <F label="Punkte-Kosten" v={edit.points_cost} on={(v) => up("points_cost")(parseInt(v) || 0)} type="number" />
          <F label="Max. Teilnahmen / Mitglied" v={edit.max_entries_per_member} on={(v) => up("max_entries_per_member")(parseInt(v) || 1)} type="number" />
          <F label="Gewinn-Gutschein (€)" v={edit.winner_voucher_value} on={(v) => up("winner_voucher_value")(parseFloat(v) || 0)} type="number" step="0.5" />
          <label className="flex items-center gap-2 mt-6">
            <input type="checkbox" checked={!!edit.is_active} onChange={(e) => up("is_active")(e.target.checked)} className="accent-[#D4AF37]" />
            <span className="text-sm">Aktiv (im Mitgliederportal sichtbar)</span>
          </label>
        </div>
        <div className="mt-6 flex gap-2 justify-end">
          <button onClick={() => setEdit(null)} className="px-4 py-2 border border-[#2A2A2A] rounded text-[11px] uppercase tracking-widest">Abbrechen</button>
          <button onClick={save} data-testid="contest-save-btn"
            className="inline-flex items-center gap-2 bg-[#D4AF37] hover:bg-[#EEDC82] text-black px-5 py-2 rounded text-[11px] uppercase tracking-widest">
            <Save size={12} /> Speichern
          </button>
        </div>
      </div>
    </div>
  );
}

function F({ label, v, on, type = "text", textarea, rows, placeholder, testid, full, step }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <label className="text-[10px] uppercase tracking-widest text-[#A8A398]">{label}</label>
      {textarea ? (
        <textarea rows={rows || 3} value={v || ""} onChange={(e) => on(e.target.value)} data-testid={testid} placeholder={placeholder}
          className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
      ) : (
        <input type={type} step={step} value={v ?? ""} onChange={(e) => on(e.target.value)} data-testid={testid} placeholder={placeholder}
          className="w-full mt-1 bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm focus:border-[#D4AF37] outline-none" />
      )}
    </div>
  );
}
